import {
  fieldTypesWithCalculatedTransformation,
  fieldTypesWithLocallyDerivedTransformation,
  FieldPopulateMethod,
  listFieldTypes,
} from 'aqTypes';
import { isTruthy } from '@ardoq/common-helpers';
import {
  APIFieldAttributes,
  APIFieldType,
  APIGlobalField,
  LocallyDerivedTransformation,
  LocallyDerivedTransformationOperation,
  FieldGlobalAttributes,
  WebSocketGlobalFieldRenamedPayload,
} from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { Features, hasFeature } from '@ardoq/features';
import * as encodingUtils from '@ardoq/html';
import { type I18nLocale, t } from '@ardoq/i18n';
import { pick } from 'lodash';

/* ## Module design
The idea behind this "operation" module is to have a set of pure functions
to delegate the responsibility of the field data structure so:
 - other modules can work at a higher abstraction level
 - clarify where to search for and add new operations

To illustrate what belongs inside this file, imagine we change:
 - the data structure of field from an object to a Map, or
 - rename a field like name or componentType
How many files do we have to change? If the design is implemented to its
limit the answer should be 1. This means any access on the field data
structure happens through this module, eg. `fieldOps.getName(field)`
instead of `field.name`

This module *only* knows about the field type, which means avoid using other
domain objects, create a dedicated module instead. */

const getAvailableFieldTypes = (): APIFieldType[] => {
  // Note that the order is significant, it corresponds to the order field types
  // appear in the dropdown.
  let fieldTypes = [
    APIFieldType.TEXT,
    APIFieldType.TEXT_AREA,
    APIFieldType.DATE_ONLY,
    APIFieldType.DATE_TIME,
    APIFieldType.CHECKBOX,
    APIFieldType.NUMBER,
    APIFieldType.USER,
    APIFieldType.EMAIL,
    APIFieldType.URL,
    APIFieldType.FILE,
    APIFieldType.LIST,
    APIFieldType.SELECT_MULTIPLE_LIST,
    APIFieldType.DATE_ONLY_RANGE,
    APIFieldType.DATE_TIME_RANGE,
  ];

  if (hasFeature(Features.DISABLE_DATE_TIME_RANGE)) {
    fieldTypes = fieldTypes.filter(
      fieldType => fieldType !== APIFieldType.DATE_TIME_RANGE
    );
  }

  return fieldTypes;
};

const isCalculatedField = (
  field: APIGlobalField | APIFieldAttributes
): boolean => field.calculatedFieldSettings?.storedQueryId !== undefined;

const SYNTHETIC_PARTIAL_CALCULATED_FIELD_QUERY_ID =
  'client_side_synthetic-partial_calculated_field_query_id';

const isCalculatedFieldWithoutQuery = (
  field: APIGlobalField | APIFieldAttributes
): boolean =>
  field.calculatedFieldSettings?.storedQueryId ===
  SYNTHETIC_PARTIAL_CALCULATED_FIELD_QUERY_ID;

const isLocallyDerivedField = (
  field: APIGlobalField | APIFieldAttributes
): boolean => field.calculatedFieldSettings?.locallyDerived !== undefined;

const toSetOfCalculatedFieldNames = (
  fields: Array<APIGlobalField | APIFieldAttributes>
) => {
  const calculatedFieldNames = fields
    .filter(isCalculatedField)
    .map(field => field.name);

  return new Set(calculatedFieldNames);
};

type FieldTypeLabelEntry = { key: string; default: string };

/**
 * Field type mapping
 * Internal data structure, do not export!
 */
const fieldTypeLabels: Record<APIFieldType, FieldTypeLabelEntry> = {
  [APIFieldType.TEXT]: { key: 'fieldTypes:type.text', default: 'Text' },
  [APIFieldType.TEXT_AREA]: {
    key: 'fieldTypes:type.textArea',
    default: 'Text paragraph',
  },
  [APIFieldType.DATE_ONLY]: {
    key: 'fieldTypes:type.dateOnly',
    default: 'Date',
  },
  [APIFieldType.DATE_TIME]: {
    key: 'fieldTypes:type.dateTime',
    default: 'Date time',
  },
  [APIFieldType.CHECKBOX]: {
    key: 'fieldTypes:type.checkbox',
    default: 'Checkbox',
  },
  [APIFieldType.NUMBER]: { key: 'fieldTypes:type.number', default: 'Number' },
  [APIFieldType.USER]: { key: 'fieldTypes:type.user', default: 'User' },
  [APIFieldType.EMAIL]: { key: 'fieldTypes:type.email', default: 'Email' },
  [APIFieldType.URL]: { key: 'fieldTypes:type.url', default: 'URL' },
  [APIFieldType.LIST]: { key: 'fieldTypes:type.list', default: 'List' },
  [APIFieldType.SELECT_MULTIPLE_LIST]: {
    key: 'fieldTypes:type.selectMultipleList',
    default: 'Select multiple list',
  },
  [APIFieldType.DATE_ONLY_RANGE]: {
    key: 'fieldTypes:type.dateOnlyRange',
    default: 'Date range',
  },
  [APIFieldType.DATE_TIME_RANGE]: {
    key: 'fieldTypes:type.dateTimeRange',
    default: 'Date time range',
  },
  [APIFieldType.FILE]: { key: 'fieldTypes:type.file', default: 'File' },
};

const getFieldTypeLabel = (
  i18nLocale: I18nLocale,
  fieldType: APIFieldType
): string => {
  const entry = fieldTypeLabels[fieldType];
  return t(i18nLocale, entry.key, entry.default);
};

const getAvailablePopulateMethods = (
  fieldType: APIFieldType,
  canCreateGraphFilter: boolean
): FieldPopulateMethod[] => {
  const methods = [FieldPopulateMethod.MANUAL];
  if (
    canCreateGraphFilter &&
    fieldTypesWithCalculatedTransformation.has(fieldType)
  ) {
    methods.push(FieldPopulateMethod.CALCULATED);
  }
  if (
    canCreateGraphFilter &&
    fieldTypesWithLocallyDerivedTransformation.has(fieldType)
  ) {
    methods.push(FieldPopulateMethod.LOCALLY_DERIVED);
  }

  return methods;
};
const getPopulateMethodFromField = (
  field: APIGlobalField | APIFieldAttributes
): FieldPopulateMethod => {
  if (isCalculatedField(field)) {
    return FieldPopulateMethod.CALCULATED;
  } else if (isLocallyDerivedField(field)) {
    return FieldPopulateMethod.LOCALLY_DERIVED;
  }
  return FieldPopulateMethod.MANUAL;
};

const fieldPopulateMethodLabels: Record<
  FieldPopulateMethod,
  FieldTypeLabelEntry
> = {
  [FieldPopulateMethod.MANUAL]: {
    key: 'fieldTypes:populateMethod.manual',
    default: 'Manual',
  },
  [FieldPopulateMethod.CALCULATED]: {
    key: 'fieldTypes:populateMethod.calculated',
    default: 'Calculated',
  },
  [FieldPopulateMethod.LOCALLY_DERIVED]: {
    key: 'fieldTypes:populateMethod.locallyDerived',
    default: 'Reactive',
  },
};

const getFieldPopulateMethodLabel = (
  i18nLocale: I18nLocale,
  populateMethod: FieldPopulateMethod
): string => {
  const entry = fieldPopulateMethodLabels[populateMethod];
  return t(i18nLocale, entry.key, entry.default);
};

const getFieldTypeLabelWithPopulateMethod = (
  i18nLocale: I18nLocale,
  field: APIGlobalField | APIFieldAttributes
): string => {
  const isTransformedField =
    isCalculatedField(field) || isLocallyDerivedField(field);
  const typeLabel = fieldOps.getFieldTypeLabel(i18nLocale, field.type);
  if (!isTransformedField) return typeLabel;
  const populateMethodLabel = fieldOps.getFieldPopulateMethodLabel(
    i18nLocale,
    fieldOps.getPopulateMethodFromField(field)
  );
  return t(
    i18nLocale,
    'fieldTypes:labelWithTypeAndPopulateMethod',
    { type: typeLabel, populateMethod: populateMethodLabel },
    '{{type}} ({{populateMethod}})'
  );
};

const getLocallyDerivedTransformations = (
  field: APIGlobalField | APIFieldAttributes
): LocallyDerivedTransformation[] =>
  field.calculatedFieldSettings?.locallyDerived ?? [];

const getLocallyDerivedOperation = (
  field: APIGlobalField | APIFieldAttributes
): LocallyDerivedTransformationOperation | undefined =>
  getLocallyDerivedTransformations(field)[0]?.operation;

/**
 * Use to sort fields by their labels alphabetical order
 */
const compareFieldsByLabelAsc = (
  a: APIGlobalField | APIFieldAttributes | FieldGlobalAttributes,
  b: APIGlobalField | APIFieldAttributes | FieldGlobalAttributes
) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

/**
 * The field's label + a type annotation
 */
const getLabelWithTypeAnnotation = (
  i18nLocale: I18nLocale,
  field: APIGlobalField | APIFieldAttributes
): string => {
  const fieldType = getFieldTypeLabel(i18nLocale, field.type);
  return t(
    i18nLocale,
    'fieldTypes:labelWithType',
    { label: field.label, type: fieldType },
    '{{label}} ({{type}})'
  );
};

const getTooltip = (field: APIGlobalField | APIFieldAttributes): string => {
  const transformation: LocallyDerivedTransformation | undefined =
    getLocallyDerivedTransformations(field)[0];

  if (
    transformation?.operation === LocallyDerivedTransformationOperation.MATH
  ) {
    return `Calculated as ${transformation.config.expression.replaceAll('@', '')}`;
  }

  if (
    transformation?.operation ===
    LocallyDerivedTransformationOperation.DICTIONARY_LOOKUP
  ) {
    return `Calculated from ${transformation.config.additionalFields[0]}`;
  }

  if (
    transformation?.operation === LocallyDerivedTransformationOperation.CONCAT
  ) {
    return `Calculated from ${transformation.config.additionalFields.join(', ')}`;
  }

  return '';
};

const compareFieldsByOrderAsc = (
  fieldA: APIFieldAttributes,
  fieldB: APIFieldAttributes
) => {
  if (dateRangeOperations.areMatchingDateTimeFields(fieldA, fieldB)) {
    return dateRangeOperations.compareDateRangeFieldLabels(
      fieldA.label,
      fieldB.label
    );
  }
  return fieldA._order - fieldB._order;
};

const getDescription = (field: APIGlobalField | APIFieldAttributes) =>
  encodingUtils.escapeHTML(field.description || '').replace(/\n/gi, '<br>');

const toFieldNames = (fields: APIFieldAttributes[]): Set<string> =>
  new Set(fields.map(({ name }) => name));

export type FieldLabelByName = Record<string, string>;

const toGlobalField = (field: APIFieldAttributes): FieldGlobalAttributes =>
  pick(
    field,
    'name',
    'description',
    'label',
    'type',
    'calculatedFieldSettings',
    'defaultValue',
    'numberFormatOptions'
  );

const withoutOptions = (value: unknown, options: Set<string>): unknown => {
  if (!Array.isArray(value)) {
    return options.has(value as string) ? undefined : value;
  }
  const kept = value.filter(option => !options.has(option));
  return kept.length === value.length ? value : kept;
};

type RenamedField = { oldName: string; newName: string; label: string };

const toRenamedFields = ({
  oldName,
  newName,
  label,
  dateRange,
}: WebSocketGlobalFieldRenamedPayload): RenamedField[] =>
  dateRange
    ? [
        {
          oldName: dateRangeOperations.toStartDateName(oldName),
          newName: dateRangeOperations.toStartDateName(newName),
          label: dateRangeOperations.toStartDateLabel(label),
        },
        {
          oldName: dateRangeOperations.toEndDateName(oldName),
          newName: dateRangeOperations.toEndDateName(newName),
          label: dateRangeOperations.toEndDateLabel(label),
        },
      ]
    : [{ oldName, newName, label }];

const withGlobalAttributes = (
  field: APIFieldAttributes,
  {
    label,
    type,
    description,
    defaultValue,
    calculatedFieldSettings,
    numberFormatOptions,
  }: APIGlobalField
): APIFieldAttributes => ({
  ...field,
  label,
  type,
  description: description ?? null,
  defaultValue,
  calculatedFieldSettings,
  numberFormatOptions,
});

const getRawLabel = (field: APIGlobalField | APIFieldAttributes): string =>
  field.label || field.name;

const hasComponentType = (field: APIFieldAttributes, typeId: string): boolean =>
  Boolean(field.componentType.includes(typeId));

const hasReferenceType = (field: APIFieldAttributes, typeId: string): boolean =>
  Boolean(field.referenceType.includes(typeId));

const isListField = (field: APIGlobalField | APIFieldAttributes): boolean =>
  listFieldTypes.includes(field.type);

const getAllowedValues = (
  field: APIGlobalField | APIFieldAttributes
): string[] => {
  if (!isListField(field)) return [];
  const defaultValue = field.defaultValue;
  if (!defaultValue || typeof defaultValue !== 'string') return [];
  return defaultValue
    .split(',')
    .filter(isTruthy)
    .map((value: string) => value.trim());
};

// Should be the only export aside from types
export const fieldOps = {
  compareFieldsByLabelAsc,
  compareFieldsByOrderAsc,
  getAllowedValues,
  getAvailableFieldTypes,
  getAvailablePopulateMethods,
  getDescription,
  getFieldPopulateMethodLabel,
  getFieldTypeLabel,
  getFieldTypeLabelWithPopulateMethod,
  getLabelWithTypeAnnotation,
  getLocallyDerivedOperation,
  getLocallyDerivedTransformations,
  getPopulateMethodFromField,
  getRawLabel,
  getTooltip,
  hasComponentType,
  hasReferenceType,
  isCalculatedField,
  isCalculatedFieldWithoutQuery,
  isListField,
  isLocallyDerivedField,
  toFieldNames,
  toGlobalField,
  toRenamedFields,
  toSetOfCalculatedFieldNames,
  withGlobalAttributes,
  withoutOptions,
};
